import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { ApolloError, ApolloQueryResult, useReactiveVar } from "@apollo/client";
import { OverlayTrigger, Tooltip, Popover, Row, Button, Col, Container } from "react-bootstrap";
import i18next from "i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import {
    AddAccessRightsModal,
    AddEmployeeFromAnotherBranchModal,
    EmployeeLimitModal,
    Pagination,
    WithModulesAccess,
    WithRoleAccess,
} from "@services/ui-components";
import { renderScheduleStatus } from "../utils/dateUtils";
import BookingSettingsModal from "../modals/BookingSettingsModal/BookingSettingsModal";
import DeleteEmployeeModal from "../modals/DeleteEmployeeModal/DeleteEmployeeModal";
import RatingStars from "@services/ui-components/src/RatingStars/RatingStars";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import EmployeeCell from "@services/ui-components/src/EmployeeCell/EmployeeCell";
import ErrorService from "../../../services/ErrorService";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import SocketConnection from "../../../services/SocketConnection";
import SalaryModal from "../modals/SalaryModal/SalaryModal";
import {
    useChangeTariffBlockingMutation,
    useGetEmployeesQuery,
    useGetEmployeesTariffBlockingsQuery,
} from "@me-team/host/main/employeeList/graphql/employeeList.hooks";
import Joyride from "react-joyride";
import { getJoyrideSteps } from "../../Joyride/joyrideSteps";
import { handleJoyrideCallback } from "../../Joyride/joyrideCallbackHandler";
import AccessService from "@me-team/host/services/AccessService";
import { AllModulesTagName, ImportEntityType } from "../../../utils/enums";
import { Debounce } from "../../../services/Debounce";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import {
    CompanyAccess,
    EmployeeProxy,
    PermissionScope,
    UserBranchRelationInput,
} from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { FormDataAccessRights } from "@services/ui-components/src/Modal/EmployeeAccessToBranchModal/types";
import {
    GetEmployeesNotInBranchQuery,
    useCreateUpdateUserBranchRelationMutation,
} from "@me-team/host/main/setUpAccessRole/graphql/setUpAccessRole.hooks";
import { useEmployee } from "@me-team/common/src/hooks/useEmployee";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { DeepPartial } from "@apollo/client/utilities";
import { EmployeesListAccess } from "../types/employeesListAccess";

export interface Filter {
    name: string;
    positionId: string | number;
    statusId: string | number;
    branchId: number;
}

type RowActionAccessMode = "hidden" | "view" | "edit";

const EmployeesTable: React.FC<{
    isShowAddEmployeeFromAnotherBranchModal: boolean;
    onCloseAddEmployeeFromAnotherBranch: () => void;
    employeesWithoutAccessCurrentBranch: DeepPartial<EmployeeProxy[]>;
    filter: Filter;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    isPremiumTariff: boolean;
    employeesListAccess: EmployeesListAccess;
    position: string | number;
    goToNextStep: () => void;
    goToStep: (direction: string, newStep?: number) => void;
    run: boolean;
    stepIndex: number;
    setStepIndex: (i: number) => void;
    refetchEmployeesNotInBranch: () => Promise<ApolloQueryResult<GetEmployeesNotInBranchQuery>>;
}> = ({
    filter,
    currentPage,
    setCurrentPage,
    isPremiumTariff,
    employeesListAccess,
    goToNextStep,
    goToStep,
    run,
    stepIndex,
    setStepIndex,
    isShowAddEmployeeFromAnotherBranchModal,
    onCloseAddEmployeeFromAnotherBranch,
    employeesWithoutAccessCurrentBranch,
    refetchEmployeesNotInBranch,
}) => {
    const { t } = useTranslation();
    const { branchId, userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const [itemsPerPage] = useState(20);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(null);
    const [isLoadingRefetchServices, setIsLoadingRefetchServices] = useState<boolean>(false);
    const { showToast, toggleShowToast, setShowToast } = useToast();
    const [selectedEmployeeToAdd, setSelectedEmployeeToAdd] = useState(null);
    const [showAddAccessRightsModal, setShowAddAccessRightsModal] = useState(false);
    const [showEmployeeLimitModal, setShowEmployeeLimitModal] = useState(false);
    const hasServiceProvidesAccess = useModulesAccess([AllModulesTagName.SERVICE_PROVIDES]);

    const handleCloseEmployeeLimitModal = () => {
        setShowEmployeeLimitModal((prevState) => !prevState);
    };

    const {
        showSalaryModal,
        setShowSalaryModal,
        handleShowSalaryModal,
        selectedEmployee,
        deleteModalShow,
        setDeleteModalShow,
        showBookingSettingsModal,
        handleToggleBookingSettingsModal,
        bookingSettings,
        deleteEmployee,
        tooltipResetKey,
    } = useEmployee();

    useScrollToTopOnChange(currentPage);

    const [createUserBranchRelation] = useCreateUpdateUserBranchRelationMutation({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const handleToggleAddAccessRightsModal = () => setShowAddAccessRightsModal((prevState) => !prevState);
    const queryEmployeeId = employeesListAccess.list.shouldShowOnlySelf
        ? (currentBranchEmployeeId ?? null)
        : userId
          ? +userId
          : null;
    const queryEmployeeEmail = employeesListAccess.list.shouldShowOnlySelf ? null : location?.state?.mail;
    const queryBranchId = filter.branchId !== -1 ? filter.branchId : null;
    const isSelfOnlyListPending = employeesListAccess.list.shouldShowOnlySelf && !currentBranchEmployeeId;
    const canShowAccessSwitchColumn = !isPremiumTariff && employeesListAccess.accessSwitches.canView;

    const { data, loading, refetch } = useGetEmployeesQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            name: filter.name ? filter.name : null,
            positionId: filter.positionId ? +filter.positionId : null,
            statusId: filter.statusId ? +filter.statusId : null,
            id: queryEmployeeId,
            email: queryEmployeeEmail,
            companyId: +currentCompanyId,
            branchId: queryBranchId,
            deleted: false,
        },
        skip: !currentCompanyId || employeesListAccess.isRoleAccessPending || isSelfOnlyListPending,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        context: {
            errorType: "global",
        },
    });

    const {
        data: employeesOnlyActive,
        loading: loadingTariffBlocking,
        refetch: refetchTariffBlocking,
    } = useGetEmployeesTariffBlockingsQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const handleRefetch = useCallback(() => {
        setIsLoadingRefetchServices(true);
        refetch().finally(() => {
            setIsLoadingRefetchServices(false);
        });
    }, [refetch]);

    const debouncedRefetch = useMemo(() => Debounce(handleRefetch, 1000), [handleRefetch]);

    useEffect(() => {
        if (currentCompanyId) {
            new SocketConnection(`${process.env.REACT_APP_SOCKET}/?companyId=${currentCompanyId}`, (data: any) => {
                if (data.entity === ImportEntityType.EMPLOYEE_TYPE) {
                    debouncedRefetch();
                }
            });
        }
    }, [currentCompanyId, debouncedRefetch]);

    useEffect(() => {
        const navigationStep = +location.state?.navigationStep === 4;
        const hasPremiumTariff = AccessService.getInstance().isPremium;
        const element = document.querySelector("#table");
        if (element && navigationStep && hasPremiumTariff) {
            element.scrollLeft = element.scrollWidth - element.clientWidth;
        }
    }, [loading, loadingTariffBlocking, location.state?.navigationStep]);
    const widthInner = window?.innerWidth;
    const [changeAccess] = useChangeTariffBlockingMutation();
    const employees = data?.user.company.employees.employees;
    const pageCount = data?.user?.company?.employees?.pageCount;
    const totalItems = data?.user?.company?.employees?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const isSelfRow = useCallback(
        (employee?: EmployeeProxy | null) =>
            typeof employee?.id === "number" && currentBranchEmployeeId === employee.id,
        [currentBranchEmployeeId]
    );
    const isOwnerRow = useCallback((employee?: EmployeeProxy | null) => Boolean(employee?.role?.isOwner), []);
    const hasScopedViewAccess = useCallback(
        (
            employee: EmployeeProxy,
            access: {
                canViewAll: boolean;
                canViewSelf: boolean;
            }
        ) => access.canViewAll || (access.canViewSelf && isSelfRow(employee)),
        [isSelfRow]
    );
    const hasScopedEditAccess = useCallback(
        (
            employee: EmployeeProxy,
            access: {
                canEditAll: boolean;
                canEditSelf: boolean;
            }
        ) => access.canEditAll || (access.canEditSelf && isSelfRow(employee)),
        [isSelfRow]
    );
    const resolveRowActionAccessMode = useCallback(
        (
            employee: EmployeeProxy,
            access: {
                canViewAll: boolean;
                canViewSelf: boolean;
                canEditAll: boolean;
                canEditSelf: boolean;
            }
        ): RowActionAccessMode => {
            if (hasScopedEditAccess(employee, access)) {
                return "edit";
            }

            if (hasScopedViewAccess(employee, access)) {
                return "view";
            }

            return "hidden";
        },
        [hasScopedEditAccess, hasScopedViewAccess]
    );
    const canToggleAccessSwitch = useCallback(
        (employee: EmployeeProxy) =>
            employeesListAccess.accessSwitches.canManage &&
            employee?.status?.id !== 4 &&
            !isSelfRow(employee) &&
            !isOwnerRow(employee),
        [employeesListAccess.accessSwitches.canManage, isOwnerRow, isSelfRow]
    );
    const getSalaryAccessMode = useCallback(
        (employee?: EmployeeProxy | null): RowActionAccessMode =>
            employee ? resolveRowActionAccessMode(employee, employeesListAccess.salaryAccess) : "hidden",
        [employeesListAccess.salaryAccess, resolveRowActionAccessMode]
    );
    const getBookingAccessMode = useCallback(
        (employee?: EmployeeProxy | null): RowActionAccessMode =>
            employee ? resolveRowActionAccessMode(employee, employeesListAccess.bookingAccess) : "hidden",
        [employeesListAccess.bookingAccess, resolveRowActionAccessMode]
    );
    const selectedEmployeeSalaryAccessMode = getSalaryAccessMode(selectedEmployee);
    const selectedEmployeeBookingAccessMode = getBookingAccessMode(selectedEmployee);
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const getStatusClass = (statusId: number) => {
        switch (statusId) {
            case 2:
                return "bg-green";
            case 4:
                return "bg-danger";
            case 3:
                return "bg-grey";
            case 1:
                return "bg-orange";
            default:
                return "";
        }
    };

    const handleAccess = async (values: EmployeeProxy) => {
        const variables = {
            id: values.id,
            name: values.name,
            surname: values.surname,
            email: values.email,
            phone: values.phone,
            specialization: values.specialization,
            employeePosition: values.position.id,
            role: values.role.id,
            status: values.status.id,
            tariffBlocking: !values.tariffBlocking,
            manualBlocking: !values.tariffBlocking,
            isUpdateAccess: true,
            companyId: +currentCompanyId,
            branchId: +branchId,
        };
        try {
            const tariffDisabled: boolean = values.tariffBlocking;
            const isEmployeeLimitExceeded =
                tariffDisabled &&
                !AccessService.getInstance().canAddEmployee(
                    employeesOnlyActive?.user?.company?.employees?.employees,
                    values.role.isOwner ?? false
                );

            if (isEmployeeLimitExceeded) {
                setShowEmployeeLimitModal(true);
            } else {
                await changeAccess({
                    variables,
                    context: {
                        errorType: "local",
                    },
                    onError: (error: ApolloError) => {
                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                    },
                    onCompleted: async () => {
                        await refetchTariffBlocking();
                        await refetch();
                        toggleShowToast();
                    },
                });
            }
        } catch (error) {
            console.error("Error creating Employee:", error);
        }
    };

    const handleAddAccessRightsNewEmployee = async (values: FormDataAccessRights) => {
        const input: UserBranchRelationInput = {
            branchId: values.branchId,
            employeeId: values.employeeId,
            roleId: values.roleId,
        };
        currentCompanyId &&
            (await createUserBranchRelation({
                variables: {
                    companyId: +currentCompanyId,
                    input: [input],
                    branchId: +branchId,
                },
                onCompleted: async (data) => {
                    await refetchEmployeesNotInBranch();
                    data && (await refetch());
                },
                context: {
                    errorType: "local",
                },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            }));
        handleToggleAddAccessRightsModal();
    };

    if (
        loading ||
        loadingTariffBlocking ||
        isLoadingRefetchServices ||
        !currentCompanyId ||
        employeesListAccess.isRoleAccessPending ||
        isSelfOnlyListPending
    ) {
        return <Spinner />;
    }

    return (
        <>
            {run && stepIndex >= 0 && stepIndex <= 7 && stepIndex !== 2 && (
                <Joyride
                    steps={getJoyrideSteps({ goToNextStep, goToStep, booleanValue: !employees?.[0]?.tariffBlocking })}
                    run={run}
                    stepIndex={stepIndex}
                    continuous={true}
                    scrollToFirstStep={true}
                    showProgress={true}
                    showSkipButton={true}
                    spotlightClicks={true}
                    callback={(data) => handleJoyrideCallback(data, setStepIndex)}
                    tooltipComponent={({ step }) => <div>{step.content}</div>}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={t("Saved") || ""} />}
            {!isPremiumTariff && (
                <div
                    className="bg-danger-light text-dark border-start border-4 border-danger rounded fs-7 py-6 px-4"
                    style={{ margin: "36px 0 36px 0" }}
                >
                    {t(
                        "With your tariff, only inclusion of 7 employees is available in the company (6 employees and 1 owner). You can enable/disable access for any 7 from the list of created employees. If you need more, upgrade your tariff."
                    )}{" "}
                    <Link to={`/payment/company/${currentCompanyId}/tariff/${branchId}`}>Premium</Link>.
                    <span className="ms-1">
                        {t(
                            "Disabled employees are only available on this" +
                                " page and will not" +
                                " be" +
                                " displayed in the appointments log, work schedule, online booking, and" +
                                " other me-pos pages."
                        )}
                    </span>
                </div>
            )}
            {employees?.length === 0 ? (
                <Container className="mt-5">
                    <Row className="mt-5">
                        <Col xs={12}>
                            <h4 className="head-personal-data text-center fw-normal pt-5 mt-5">
                                {t("NoDataFoundForTheGivenValues")}
                            </h4>
                        </Col>
                    </Row>
                </Container>
            ) : (
                <div id={"table"} className={run && widthInner > 630 ? "table-responsive scrollbar " : "overflow-auto"}>
                    <table className="table">
                        <thead>
                            <tr className="fs-7">
                                {canShowAccessSwitchColumn && (
                                    <th className="text-nowrap">
                                        {t("Access")}{" "}
                                        <OverlayTrigger
                                            key={"bottom"}
                                            placement={"bottom"}
                                            overlay={
                                                <Popover id="popover-contained">
                                                    <Popover.Body>
                                                        <Row>
                                                            <p className="m-0">
                                                                {" "}
                                                                On -{" "}
                                                                {t(
                                                                    "On - the employee is enabled, they are displayed in the work schedule, and available for online booking."
                                                                )}
                                                            </p>
                                                        </Row>
                                                        <Row className="row">
                                                            <p className="m-0">
                                                                {" "}
                                                                <span className=" text-secondary"> Off</span>{" "}
                                                                {t(
                                                                    "- the employee is disabled, they are only displayed on this page."
                                                                )}
                                                            </p>
                                                        </Row>
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <i className="bi bi-info-circle"> </i>
                                        </OverlayTrigger>{" "}
                                    </th>
                                )}
                                <th className="text-nowrap">
                                    {t("Status")}{" "}
                                    <OverlayTrigger
                                        key={"bottom"}
                                        placement={"bottom"}
                                        overlay={
                                            <Popover id="popover-contained" className=" popover-max-width">
                                                <Popover.Body>
                                                    <p className="m-0">
                                                        <span className=" text-green">
                                                            <i className="bi bi-square-fill"></i> {t("Active")}{" "}
                                                        </span>
                                                        {t(
                                                            "- has a schedule, free time, and available for online booking."
                                                        )}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className=" text-orange">
                                                            <i className="bi bi-square-fill"></i> {t("Busy")}{" "}
                                                        </span>
                                                        {t(
                                                            "- the employee has no available slots for booking throughout their scheduled work days."
                                                        )}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className=" text-grey">
                                                            <i className="bi bi-square-fill"></i> {t("Hidden")}{" "}
                                                        </span>
                                                        {t("- hidden from the online booking form.")}{" "}
                                                    </p>
                                                    <p className="m-0">
                                                        <span className=" text-danger">
                                                            <i className="bi bi-square-fill"></i> {t("Fired")}{" "}
                                                        </span>
                                                        {t("- the employee has been fired.")}
                                                    </p>
                                                </Popover.Body>
                                            </Popover>
                                        }
                                    >
                                        <i className="bi bi-info-circle"> </i>
                                    </OverlayTrigger>
                                </th>
                                <th>{t("Employee")}</th>
                                <WithModulesAccess modules={[AllModulesTagName.SERVICES]}>
                                    <th>{t("Provides Services")}</th>
                                </WithModulesAccess>
                                <th>{t("Branches")}</th>
                                <th className="text-nowrap">
                                    {t("Schedule")}{" "}
                                    <OverlayTrigger
                                        key={"bottom"}
                                        placement={"bottom"}
                                        overlay={
                                            <Popover>
                                                <Popover.Body>
                                                    <Row>
                                                        <p className="m-0 mb-1">
                                                            <span className=" text-green">
                                                                <i className="bi bi-square"></i> {t("Installed")}{" "}
                                                            </span>{" "}
                                                            {t("- if the schedule is set.")}{" "}
                                                        </p>
                                                    </Row>

                                                    <p className="m-0">
                                                        <span className=" text-orange">
                                                            <i className="bi bi-square"></i> {t("Missed")}
                                                        </span>{" "}
                                                        {t("- possible in cases:")}
                                                        <ul className="m-0">
                                                            <li>
                                                                {t(
                                                                    "the schedule for the employee has not been installed;"
                                                                )}
                                                            </li>
                                                            <li>{t("the schedule has expired;")}</li>
                                                        </ul>
                                                    </p>
                                                </Popover.Body>
                                            </Popover>
                                        }
                                    >
                                        <i className="bi bi-info-circle"> </i>
                                    </OverlayTrigger>
                                </th>
                                <WithModulesAccess modules={[AllModulesTagName.REVIEWS]}>
                                    <th>{t("Rating")}</th>
                                </WithModulesAccess>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees?.map((i: EmployeeProxy, index) => {
                                const rowScope = isSelfRow(i) ? PermissionScope.Self : PermissionScope.All;
                                const salaryAccessMode = getSalaryAccessMode(i);
                                const bookingAccessMode = getBookingAccessMode(i);
                                const isSelfEmployee = isSelfRow(i);
                                const isOwnerEmployee = isOwnerRow(i);
                                const isFiredEmployee = i?.status?.id === 4;
                                const isTariffBlockedEmployee = i?.tariffBlocking;
                                const isAccessSwitchDisabled = !canToggleAccessSwitch(i);
                                const canOpenEmployeeServicesLink =
                                    hasServiceProvidesAccess && !isTariffBlockedEmployee;
                                const isScheduleActionDisabled = isFiredEmployee || isTariffBlockedEmployee;
                                const isBookingActionDisabled = isFiredEmployee || isTariffBlockedEmployee;
                                const isDismissActionDisabled = isOwnerEmployee || isSelfEmployee;
                                const canShowSalaryAction = salaryAccessMode !== "hidden" && !isTariffBlockedEmployee;
                                const canShowEmployeeServicesAction = !isTariffBlockedEmployee;
                                const canShowScheduleAction = !isScheduleActionDisabled;
                                const canShowBookingAction = bookingAccessMode !== "hidden" && !isBookingActionDisabled;
                                const canShowDismissAction = !isDismissActionDisabled;
                                return (
                                    <tr key={i?.id}>
                                        {canShowAccessSwitchColumn && (
                                            <td className={`align-middle ${index === 0 ? "access-switcher" : ""}`}>
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id={`flexSwitchCheckCheckedDisabled-${i.id}`}
                                                        checked={!isTariffBlockedEmployee}
                                                        onChange={() => !isAccessSwitchDisabled && handleAccess(i)}
                                                        disabled={isAccessSwitchDisabled}
                                                    />
                                                    {i?.status?.id === 4}
                                                </div>
                                            </td>
                                        )}
                                        <td className="align-middle">
                                            <div
                                                className={` ${getStatusClass(i?.status?.id)} text-white w-100 h-100 p-1  fw-bold text-center rounded no-hover fs-7`}
                                                style={{ cursor: "default" }}
                                            >
                                                {i?.status.name}
                                            </div>
                                        </td>
                                        <td className="align-middle">
                                            <WithRoleAccess
                                                anyPermissions={[
                                                    CompanyAccess.EmployeeView,
                                                    CompanyAccess.EmployeeEdit,
                                                ]}
                                                scope={rowScope}
                                                fallback={<EmployeeCell i={i} />}
                                            >
                                                <EmployeeCell i={i} link={true} />
                                            </WithRoleAccess>
                                        </td>
                                        <WithModulesAccess modules={[AllModulesTagName.SERVICES]}>
                                            <td className="align-middle">
                                                <WithRoleAccess
                                                    permission={CompanyAccess.EmployeeServicesView}
                                                    scope={rowScope}
                                                    fallback={<span>{i?.employeeServiceRelations.length}</span>}
                                                >
                                                    {canOpenEmployeeServicesLink ? (
                                                        <Link
                                                            to={`/employees-list/employee-services/${i.id}/${branchId}`}
                                                            className="text-decoration-none"
                                                        >
                                                            {i?.employeeServiceRelations.length}
                                                        </Link>
                                                    ) : (
                                                        <span>{i?.employeeServiceRelations.length}</span>
                                                    )}
                                                </WithRoleAccess>
                                            </td>
                                        </WithModulesAccess>
                                        <td className="align-middle">{i?.userBranchRelations.length}</td>
                                        <td className="align-middle">
                                            {renderScheduleStatus(i?.isScheduleActive, t("Missed"), t("Installed"))}
                                        </td>
                                        <WithModulesAccess modules={[AllModulesTagName.REVIEWS]}>
                                            <td className="align-middle ">
                                                <div className="d-flex flex-row">
                                                    <WithRoleAccess
                                                        permission={CompanyAccess.ReviewListView}
                                                        scope={rowScope}
                                                        fallback={<RatingStars rating={i?.reviews} />}
                                                    >
                                                        <Link
                                                            to={`/booking/review/company/${currentCompanyId}/${i.id}/${branchId}`}
                                                            className="text-decoration-none"
                                                        >
                                                            <RatingStars rating={i?.reviews} />
                                                        </Link>
                                                    </WithRoleAccess>
                                                </div>
                                            </td>
                                        </WithModulesAccess>
                                        <td className="text-end pe-0 align-middle">
                                            <div className="d-flex justify-content-end flex-nowrap">
                                                <WithRoleAccess
                                                    anyPermissions={[
                                                        CompanyAccess.EmployeeView,
                                                        CompanyAccess.EmployeeEdit,
                                                    ]}
                                                    scope={rowScope}
                                                >
                                                    <OverlayTrigger
                                                        overlay={
                                                            <Tooltip className="tooltip-category">{t("Edit")}</Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/employees-list/edit-employee/${i.id}/${branchId}`
                                                                )
                                                            }
                                                            className="btn-size text-dark m-1 p-0"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </WithRoleAccess>
                                                {canShowSalaryAction && (
                                                    <WithModulesAccess modules={[AllModulesTagName.SALARY]}>
                                                        <OverlayTrigger
                                                            overlay={
                                                                <Tooltip className="tooltip-category">
                                                                    {t("Employee Salary")}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                onClick={() => handleShowSalaryModal(i)}
                                                                className="btn-size text-dark m-1 p-0"
                                                            >
                                                                <i className="bi bi-cash-coin"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </WithModulesAccess>
                                                )}
                                                <WithModulesAccess
                                                    modules={[
                                                        AllModulesTagName.SERVICES,
                                                        AllModulesTagName.SERVICE_PROVIDES,
                                                    ]}
                                                >
                                                    <WithRoleAccess
                                                        permission={CompanyAccess.EmployeeServicesView}
                                                        scope={rowScope}
                                                    >
                                                        {canShowEmployeeServicesAction ? (
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip className="tooltip-category">
                                                                        {t("Employee services")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/employees-list/employee-services/${i.id}/${branchId}`
                                                                        )
                                                                    }
                                                                    className="btn-size text-dark m-1 p-0 d-inline-flex justify-content-center align-items-center position-relative"
                                                                >
                                                                    <i className="bi bi-bookmark"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        ) : null}
                                                    </WithRoleAccess>
                                                </WithModulesAccess>
                                                <WithModulesAccess modules={[AllModulesTagName.WORK_SCHEDULE]}>
                                                    <WithRoleAccess
                                                        permission={CompanyAccess.ScheduleCalendarView}
                                                        scope={rowScope}
                                                    >
                                                        {canShowScheduleAction ? (
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip className="tooltip-category">
                                                                        {t("Schedule")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        localStorage.setItem("step", "7");
                                                                        navigate(`/work-schedule/${i.id}/${branchId}`);
                                                                    }}
                                                                    className={`btn-size text-dark m-1 p-0 d-inline-flex justify-content-center align-items-center position-relative ${
                                                                        index === 0 ? "schedule-link-step" : ""
                                                                    }`}
                                                                >
                                                                    <i className="bi bi-calendar4-week"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        ) : null}
                                                    </WithRoleAccess>
                                                </WithModulesAccess>
                                                {canShowBookingAction && (
                                                    <WithModulesAccess modules={[AllModulesTagName.BOOKING]}>
                                                        <OverlayTrigger
                                                            key={tooltipResetKey}
                                                            overlay={
                                                                <Tooltip className="tooltip-category">
                                                                    {t("Booking Settings")}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Button
                                                                variant="outline-secondary"
                                                                className={`btn-size text-dark m-1 p-0 ${index === 0 ? "booking-btn-step" : ""}`}
                                                                onClick={() => {
                                                                    bookingSettings(i);
                                                                    goToNextStep();
                                                                }}
                                                            >
                                                                <i className="bi bi-gear"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </WithModulesAccess>
                                                )}
                                                <WithModulesAccess modules={[AllModulesTagName.EMPLOYEES]}>
                                                    <WithRoleAccess
                                                        permission={CompanyAccess.EmployeeDismiss}
                                                        scope={rowScope}
                                                    >
                                                        {canShowDismissAction ? (
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip className="tooltip-category">
                                                                        {t("Fire an employee")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="btn-size outline-secondary m-1 p-0"
                                                                    onClick={() => deleteEmployee(i)}
                                                                >
                                                                    <i className="bi bi-person-dash text-dark" />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        ) : null}
                                                    </WithRoleAccess>
                                                </WithModulesAccess>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Pagination
                            t={t}
                            pages={Number(pageCount)}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            firstItemIndex={firstItemIndex}
                            lastItemIndex={lastItemIndex}
                            totalItems={totalItems}
                        />
                    </div>
                </div>
            )}
            <DeleteEmployeeModal
                show={deleteModalShow && !!selectedEmployee}
                onHide={() => setDeleteModalShow(false)}
                selectedEmployee={selectedEmployee}
                refetch={refetch}
            />
            <BookingSettingsModal
                show={showBookingSettingsModal && !!selectedEmployee && selectedEmployeeBookingAccessMode !== "hidden"}
                toggleModal={handleToggleBookingSettingsModal}
                selectedEmployee={selectedEmployee}
                refetch={refetch}
                toggleShowToast={toggleShowToast}
                goToStep={goToStep}
                run={run}
                accessMode={selectedEmployeeBookingAccessMode}
            />
            <SalaryModal
                show={showSalaryModal && !!selectedEmployee && selectedEmployeeSalaryAccessMode !== "hidden"}
                setShowSalaryModal={setShowSalaryModal}
                employeeId={selectedEmployee}
                refetch={refetch}
                t={t}
                toggleShowToast={toggleShowToast}
                accessMode={selectedEmployeeSalaryAccessMode}
            />

            {isErrorModalOpen && (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            )}
            <AddEmployeeFromAnotherBranchModal
                isShowAddEmployeeFromAnotherBranchModal={isShowAddEmployeeFromAnotherBranchModal}
                onCloseAddEmployeeFromAnotherBranch={onCloseAddEmployeeFromAnotherBranch}
                employeesWithoutAccessCurrentBranch={employeesWithoutAccessCurrentBranch}
                onSelectedEmployeeToAdd={setSelectedEmployeeToAdd}
                toggleAddAccessRightsModal={handleToggleAddAccessRightsModal}
            />
            <AddAccessRightsModal
                isAddAccessRightsModal={showAddAccessRightsModal}
                onCloseAddAccessRightsModal={handleToggleAddAccessRightsModal}
                selectedEmployeeToAdd={selectedEmployeeToAdd}
                onAddAccessRightsEmployee={handleAddAccessRightsNewEmployee}
            />
            <EmployeeLimitModal
                showEmployeeLimitModal={showEmployeeLimitModal}
                handleCloseEmployeeLimitModal={handleCloseEmployeeLimitModal}
            />
        </>
    );
};

export default EmployeesTable;
